import { DetailFormComponentsEnum } from "@/lib/enum/detail-form-components.enum";
import { PersonLabelEnum } from "@/lib/enum/person-label.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { Form, FormConfig, IsFormable } from "@/lib/formable";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { parsePhone } from "@/lib/utility/parsePhone";
import { RulesEnum } from "@/lib/rules/rules.map";
import { $t } from "@/lib/utility/t";
import { toast } from "@/lib/utility/toast";
import { MrfiktivPersonPhoneViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";

@IsFormable
@IsFilterable
export class PersonPhoneBase implements MrfiktivPersonPhoneViewModelGen {
  _phone?: string;

  /**
   * Get the phone number in international format
   * @inheritdoc
   */
  @FormConfig({
    category: "common.nouns.phone",
    searchKeywords: ["common.nouns.phone", "objects.person.phones.phone"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      required: false,
      label: "common.nouns.phone"
    },
    rules: [RulesEnum.REQUIRED_RULE, RulesEnum.PHONE_RULE]
  })
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.person.phones.phone"
  })
  get phone(): string {
    return this._phone ?? "";
  }

  /**
   * @param value Phone number to set
   */
  set phone(value: string) {
    this._phone = parsePhone(value);
  }

  /**
   * @inheritdoc
   */
  @FormConfig({
    category: "common.nouns.phone",
    searchKeywords: ["common.nouns.phone", "objects.person.phones.label"],
    type: DetailFormComponentsEnum.AUTO_COMPLETE,
    props: {
      items: Object.values(PersonLabelEnum).map((v) => {
        return {
          text: $t(`enums.PersonLabelEnum.${v}`),
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value",
      label: "objects.person.phones.label"
    },
    clearable: true
  })
  label?: PersonLabelEnum;

  @FormConfig({
    category: "common.nouns.phone",
    searchKeywords: ["common.nouns.phone", "objects.person.phones.notes"],
    type: DetailFormComponentsEnum.TEXT_AREA,
    clearable: true,
    props: {
      label: "objects.person.phones.notes"
    }
  })
  notes?: string;

  /**
   * Construct person
   */
  constructor(obj?: Partial<PersonPhoneBase | MrfiktivPersonPhoneViewModelGen>) {
    this.phone = obj?.phone ?? "";
    this.label = obj?.label as PersonLabelEnum;
    this.notes = obj?.notes;
  }

  copyToClipboard(): void {
    navigator.clipboard.writeText(this.phone);
    toast.info($t("components.partner.PartnerReportInitializeCard.linkCopied"));
  }

  get dto(): Partial<MrfiktivPersonPhoneViewModelGen> {
    return {
      phone: this.phone,
      label: this.label,
      notes: this.notes
    };
  }
}

type IPersonPhone = PersonPhoneBase;
const PersonPhone = Form.createForClass(Filter.createForClass(PersonPhoneBase));

export { IPersonPhone, PersonPhone };
